import apiClient from "./client";
import type { components } from "@/types/api.generated";

/** Gerenciamento paginado (15/10): listas em páginas e o rascunho calculado no servidor. */

export type OrgUnitPage = components["schemas"]["OrgUnitPage"];
export type OrgUnitRow = components["schemas"]["OrgUnitRow"];
export type FacetOption = components["schemas"]["FacetOption"];
export type ActivityPage = components["schemas"]["ActivityPageResponse"];
export type PreviewResponse = components["schemas"]["PreviewResponse"];
export type ChangedItem = components["schemas"]["ChangedItem"];
export type ManagementNode = components["schemas"]["DraftOperation"]["node"];
export type ManagementVerdict =
  components["schemas"]["DraftOperation"]["verdict"];

export interface DraftFilters {
  q?: string;
  unit_types?: string[];
  countries?: string[];
  origin?: "sap" | "manual";
  priority?: string;
  country?: string;
}

export interface DraftItem {
  item_type: string;
  item_key: string;
  /** Estrutura: a unidade e os filhos pendentes (filhos ainda não carregados). */
  with_descendants?: boolean;
}

export interface DraftOperation {
  node: ManagementNode;
  verdict: ManagementVerdict;
  items: DraftItem[];
  /** "Todos os pendentes" que batem estes filtros, resolvidos no servidor. */
  all_pending?: DraftFilters;
}

export interface OrgUnitQuery {
  verdict: ManagementVerdict;
  parent?: string;
  physical?: boolean;
  q?: string;
  unitTypes?: string[];
  countries?: string[];
  origin?: "sap" | "manual";
}

export interface ActivityQuery {
  node: "sscui" | "module_config";
  verdict: ManagementVerdict;
  q?: string;
  priority?: string;
  country?: string;
  area?: string;
  scope_item?: string;
}

export type ActivityAreasResponse =
  components["schemas"]["ActivityAreasResponse"];
export type ActivityScopePage =
  components["schemas"]["ActivityScopeItemsResponse"];
export type OrgFacetsResponse = components["schemas"]["OrgFacetsResponse"];
export interface CursorWindow {
  cursor?: string | null;
  limit: number;
}
export interface PreviewDetailsQuery {
  details_node?: ManagementNode;
  details_verdict?: ManagementVerdict;
  cursor?: string | null;
  limit?: number;
}

const base = (projectId: string) =>
  `/api/projects/${projectId}/configuration-server`;
// FastAPI lê lista repetindo a chave (`unit_type=a&unit_type=b`), sem `[]`.
const paramsSerializer = { indexes: null } as const;

export async function listOrgUnits(
  projectId: string,
  query: OrgUnitQuery,
  page: CursorWindow,
  artifactId?: string,
): Promise<OrgUnitPage> {
  const response = await apiClient.get<OrgUnitPage>(
    `${base(projectId)}/management/org-units`,
    {
      params: {
        verdict: query.verdict,
        parent: query.parent,
        physical: query.physical,
        q: query.q || undefined,
        unit_type: query.unitTypes?.length ? query.unitTypes : undefined,
        country: query.countries?.length ? query.countries : undefined,
        origin: query.origin,
        cursor: page.cursor || undefined,
        limit: page.limit,
        artifact_id: artifactId,
      },
      paramsSerializer,
    },
  );
  return response.data;
}

export async function listManagementActivities(
  projectId: string,
  query: ActivityQuery,
  page: CursorWindow,
  artifactId?: string,
): Promise<ActivityPage> {
  const response = await apiClient.get<ActivityPage>(
    `${base(projectId)}/management/activities`,
    {
      params: {
        node: query.node,
        area: query.area,
        scope_item: query.scope_item,
        verdict: query.verdict,
        q: query.q || undefined,
        priority: query.priority || undefined,
        country: query.country || undefined,
        cursor: page.cursor || undefined,
        limit: page.limit,
        artifact_id: artifactId,
      },
    },
  );
  return response.data;
}

function body(
  operations: DraftOperation[],
): components["schemas"]["DraftRequest"] {
  return {
    operations: operations.map((operation) => ({
      node: operation.node,
      verdict: operation.verdict,
      items: operation.items.map((item) => ({
        ...item,
        with_descendants: !!item.with_descendants,
      })),
      all_pending: operation.all_pending
        ? { unit_types: [], countries: [], ...operation.all_pending }
        : undefined,
    })),
  };
}

export async function previewDraft(
  projectId: string,
  operations: DraftOperation[],
  artifactId?: string,
  details?: PreviewDetailsQuery,
): Promise<PreviewResponse> {
  const response = await apiClient.post<PreviewResponse>(
    `${base(projectId)}/management/preview`,
    body(operations),
    {
      params: { artifact_id: artifactId, ...details },
    },
  );
  return response.data;
}

/** Grava o rascunho inteiro numa transação (ou entra tudo, ou nada). */
export async function saveDraft(
  projectId: string,
  operations: DraftOperation[],
  artifactId?: string,
) {
  const response = await apiClient.post<components["schemas"]["SaveResponse"]>(
    `${base(projectId)}/scope-verdicts/batch`,
    body(operations),
    { params: { artifact_id: artifactId } },
  );
  return response.data;
}

export async function listActivityAreas(
  projectId: string,
  query: ActivityQuery,
  artifactId?: string,
): Promise<ActivityAreasResponse> {
  const response = await apiClient.get<ActivityAreasResponse>(
    `${base(projectId)}/management/activity-areas`,
    { params: { ...query, artifact_id: artifactId } },
  );
  return response.data;
}
export async function listActivityScopeItems(
  projectId: string,
  query: ActivityQuery,
  page: CursorWindow,
  artifactId?: string,
): Promise<ActivityScopePage> {
  const response = await apiClient.get<ActivityScopePage>(
    `${base(projectId)}/management/activity-scope-items`,
    {
      params: {
        ...query,
        cursor: page.cursor || undefined,
        limit: page.limit,
        artifact_id: artifactId,
      },
    },
  );
  return response.data;
}
export async function getActivityCounts(projectId: string) {
  const response = await apiClient.get<
    components["schemas"]["ActivityCountsResponse"]
  >(`${base(projectId)}/management/activity-counts`);
  return response.data;
}
export async function getOrgFacets(
  projectId: string,
  artifactId?: string,
): Promise<OrgFacetsResponse> {
  const response = await apiClient.get<OrgFacetsResponse>(
    `${base(projectId)}/management/org-facets`,
    { params: { artifact_id: artifactId } },
  );
  return response.data;
}
